import { useEffect, useMemo, useState } from "react";
import CreateFundingSourceModal from "admin/components/cost-management/CreateFundingSourceModal";
import FundingSourceDrawer from "admin/components/cost-management/FundingSourceDrawer";
import { getSortIcon } from "admin/components/user-management/UserTable";
import useFundingSources from "admin/hooks/useFundingSources";
import { FundingSource } from "admin/types/interfaces/funding";
import { AlertCircle, Plus, Search, X } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import useAppHeader from "~/contexts/header";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import useUserPermission from "~/hooks/useUserPermission";
import { cn } from "~/lib/utils";
import { AdminPage } from "~/pages/manifold-nextgen/components/admin/AdminPage";
import { trackEvent } from "~/utils/monitoring";

const PAGE_TITLE = "Cost Management";

export const COST_MANAGER_WRITE_PERMISSION = "edit:cost-manager-app";

type SortDirection = "asc" | "desc";
type SortableColumn = "name" | "id" | "created" | null;

const COLUMNS = [
  {
    id: "name",
    label: "Funding Source",
    className: "",
    loadingClassName: "max-w-40",
    sortable: true,
  },
  {
    id: "id",
    label: "Funding Id",
    className: "w-56",
    loadingClassName: "max-w-56",
    sortable: true,
  },
  {
    id: "created",
    label: "Created",
    className: "w-28",
    loadingClassName: "max-w-28",
    sortable: true,
  },
];

const LoadingElement = ({ loadingClassName }: { loadingClassName: string }) => (
  <div className={`h-4 bg-muted animate-pulse rounded w-full ${loadingClassName}`} />
);

const TableLoadingSkeleton = () => (
  <>
    {Array.from({ length: 10 }).map((_, index) => (
      <TableRow key={`loading-${index}`}>
        {COLUMNS.map((column) => (
          <TableCell key={column.id} className={column.className}>
            <LoadingElement loadingClassName={column.loadingClassName} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const FundingSources = ({ isNextGen = false }: { isNextGen?: boolean }) => {
  useDocumentTitle(PAGE_TITLE);
  useAppHeader({ title: PAGE_TITLE });

  useEffect(() => {
    trackEvent("admin_cost_management_viewed");
  }, []);

  const canEdit = useUserPermission(COST_MANAGER_WRITE_PERMISSION);

  const { data: fundingSources = [], isLoading, error } = useFundingSources();

  const [searchTerm, setSearchTerm] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortableColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [selectedFundingSource, setSelectedFundingSource] = useState<FundingSource | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const filteredSources = useMemo(() => {
    let filtered = fundingSources.filter((fs) => {
      const matchesSearch = fs.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesId = (fs.fundingAccountId ?? "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesSearch || matchesId;
    });

    if (sortColumn && sortDirection) {
      filtered = [...filtered].sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;

        switch (sortColumn) {
          case "name":
            aVal = a.name.toLowerCase();
            bVal = b.name.toLowerCase();
            break;
          case "id":
            aVal = (a.fundingAccountId ?? "").toLowerCase();
            bVal = (b.fundingAccountId ?? "").toLowerCase();
            break;
          case "created":
            aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [fundingSources, searchTerm, sortColumn, sortDirection]);

  const handleRowClick = (fs: FundingSource) => {
    setSelectedFundingSource(fs);
    setIsDrawerOpen(true);
  };

  const handleSort = (columnId: string) => {
    const col = COLUMNS.find((c) => c.id === columnId);
    if (!col?.sortable) return;

    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId as SortableColumn);
      setSortDirection("asc");
    }
  };

  const getSortHeaderClasses = (column: (typeof COLUMNS)[number]) => {
    const base =
      "flex items-center gap-2 cursor-pointer hover:text-foreground transition-colors select-none group";
    return sortColumn === column.id ? `${base} text-foreground` : `${base} text-muted-foreground`;
  };

  // Renders in the page for the legacy admin app, or in the nextgen header
  // (via useNextGenHeader below) — never both.
  const newFundingSourceButton = useMemo(
    () =>
      canEdit ? (
        <Button
          onClick={() => setCreateModalOpen(true)}
          size={isNextGen ? "sm" : "lg"}
          className={cn("flex items-center gap-2", isNextGen && "text-xs")}
        >
          <Plus className={isNextGen ? "h-4 w-4" : "h-5 w-5"} />
          New Funding Source
        </Button>
      ) : null,
    [canEdit, isNextGen]
  );

  useNextGenHeader(isNextGen ? { actions: [newFundingSourceButton] } : {});

  return (
    <>
      <AdminPage
        title={PAGE_TITLE}
        description="Track the funding sources that pay for platform usage, and who can charge to them"
        titleAdornment={<Badge>Beta</Badge>}
        actions={!isNextGen && newFundingSourceButton}
        constrainWidth={isNextGen}
      >
        <div>
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by funding source or id..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10 bg-background border border-border"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="p-3 border border-destructive/20 bg-destructive/10 text-destructive rounded-md text-sm flex items-center gap-2 mb-4">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>Unable to load funding sources</span>
            </div>
          )}

          <div className="rounded-lg border overflow-x-auto">
            <Table className="min-w-full [&_td]:p-3">
              <TableHeader>
                <TableRow>
                  {COLUMNS.map((col) => (
                    <TableHead
                      key={col.id}
                      className={cn(col.className, col.sortable && "cursor-pointer")}
                      {...(col.sortable && {
                        onClick: () => handleSort(col.id),
                      })}
                    >
                      {col.sortable ? (
                        <div className={getSortHeaderClasses(col)}>
                          <span>{col.label}</span>
                          <div
                            className={cn(
                              "transition-opacity",
                              sortColumn === col.id
                                ? "opacity-100"
                                : "opacity-0 group-hover:opacity-100"
                            )}
                          >
                            {getSortIcon(col.id, sortColumn, sortDirection)}
                          </div>
                        </div>
                      ) : (
                        col.label
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableLoadingSkeleton />
                ) : filteredSources.length === 0 ? (
                  <TableRow className="pointer-events-none">
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                      No funding sources found {searchTerm && `matching "${searchTerm}"`}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSources.map((fs) => (
                    <TableRow
                      key={fs.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(fs)}
                    >
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-2">{fs.name}</span>
                      </TableCell>
                      <TableCell>{fs.fundingAccountId}</TableCell>
                      <TableCell>
                        {fs.createdAt ? new Date(fs.createdAt).toLocaleDateString() : "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {fundingSources.length > 0 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Showing{" "}
                {filteredSources.length === fundingSources.length
                  ? "all"
                  : `${filteredSources.length} of ${fundingSources.length}`}{" "}
                funding sources
              </p>
            </div>
          )}
        </div>
      </AdminPage>

      <CreateFundingSourceModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />

      <FundingSourceDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fundingSource={selectedFundingSource}
      />
    </>
  );
};

export default FundingSources;
